import { PlusCircle, Trash2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import type {
  NativeSyntheticEvent,
  TextInputSubmitEditingEventData,
} from "react-native";
import { View, XStack } from "tamagui";
import { useApi } from "../core/apis";
import {
  type SortableItemWithId,
  SortableList,
} from "../core/components/SortableList";
import { useStores } from "../core/stores";
import { ModuleBlockRenderer } from "../modules/core/ModuleUIRenderer";
import { ModuleBlockSkeleton } from "../ui/Block";
import { BodySkeleton } from "../ui/Body";
import { Box } from "../ui/Box";
import { Label } from "../ui/Label";
import { PopoverConfirm } from "../ui/PopoverConfirm";
import { PopoverHelp } from "../ui/PopoverHelp";
import { Separator } from "../ui/Separator";
import { SubtextSkeleton } from "../ui/Subtext";
import { TitleSkeleton, TitleTextarea } from "../ui/Title";
import { AddCardSheet } from "./AddCardSheet";
import type SectionModel from "./SectionModel";
import type { CreateModule } from "./SectionModel";

export function isCreateModule(
  data: unknown,
): data is Omit<
  CreateModule,
  "doneType" | "type" | "initiated" | "isDraggable"
> {
  return typeof data === "object" && data !== null && "title" in data;
}

export const SectionTitle: React.FC<{ section: SectionModel }> = observer(
  ({ section }) => {
    const setTitle = (
      event: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
    ) => {
      section.setTitle(event.nativeEvent.text);
    };

    return (
      <XStack justifyContent="space-between" alignItems="center" gap="$medium">
        <TitleTextarea
          attention={!section.data.title}
          type={section.data.title ? "inline" : "border"}
          placeholder="Set title"
          defaultValue={section.data.title ?? ""}
          onSubmitEditing={setTitle}
          submitOnEnter
          flex={1}
        />
        {section.isArchivable && (
          <PopoverConfirm
            onConfirm={() => section.plan.archiveSection(section.uid)}
          >
            <Trash2 size={"$size.normal"} color={"$muted"} />
          </PopoverConfirm>
        )}
      </XStack>
    );
  },
);

export const SectionUI = observer(
  ({ isFirst, uid }: { isFirst?: boolean; uid: string }) => {
    const { sectionsStore } = useStores();
    const { sectionsApi } = useApi();

    const section = sectionsStore.byId.get(uid);

    React.useEffect(() => {
      const unsub = sectionsApi.watchItem(uid);
      return () => {
        unsub();
      };
    }, [sectionsApi, uid]);

    const renderItem = useCallback(
      (item: SortableItemWithId<{ uid: string }>) => {
        return <ModuleBlockRenderer key={item.id} uid={item.id} />;
      },
      [],
    );

    if (!section) {
      if (isFirst) {
        return (
          <Box>
            <TitleSkeleton />
            <BodySkeleton />
            <SubtextSkeleton />
          </Box>
        );
      }

      return (
        <Box>
          <TitleSkeleton />
          <Separator noMargins />
          <ModuleBlockSkeleton />
        </Box>
      );
    }

    const showTitle =
      section.plan.data.settings.useSections && section.data.isExpandable;

    return (
      <>
        <Box noPadding={!section.data.isExpandable}>
          {showTitle && <SectionTitle section={section} />}
          {showTitle && <Separator noMargins />}

          {section.data.moduleIds.length === 1 && (
            <ModuleBlockRenderer
              noPadding={!section.data.isExpandable}
              uid={section.data.moduleIds[0]}
            />
          )}

          {section.data.moduleIds.length > 1 && (
            <View>
              <SortableList
                items={section.data.moduleIds.map((id) => ({ uid: id }))}
                onChange={(items) => {
                  section.updateOrder(items.map((listItem) => listItem.id));
                }}
                renderItem={renderItem}
              />
            </View>
          )}

          {section.data.isExpandable && (
            <>
              <Separator noMargins />
              <View
                marginTop="$medium"
                onPress={() => section.setShowModulesSheet(true)}
                cursor="pointer"
                gap={"$small"}
                style={{ opacity: 0.75 }}
                alignItems="center"
              >
                <XStack gap="$small" alignItems="center">
                  <PlusCircle />
                  <Label>Add card</Label>
                  <PopoverHelp description="Cards help you plan and make decisions, create a card and set a helpful title and icon to get going" />
                </XStack>
              </View>
            </>
          )}
        </Box>

        <AddCardSheet
          section={section}
          open={section.showModulesSheet}
          onOpenChange={(visible: boolean) =>
            section.setShowModulesSheet(!!visible)
          }
        />
      </>
    );
  },
);
